import { addTime } from '../util/dates';
import { denormalisedResponseEntities } from '../util/data';
import { timeSlotsPerDate } from '../util/generators';
import { storableError } from '../util/errors';
import {
  AVAILABILITY_LOOKAHEAD_DAYS,
  getAvailabilityBadgeData,
  resolveAvailabilityTimeZone,
} from '../util/availabilityBadge';

// ================ Action types ================ //

const FETCH_AVAILABILITY_REQUEST = 'app/listingAvailability/FETCH_AVAILABILITY_REQUEST';
const FETCH_AVAILABILITY_SUCCESS = 'app/listingAvailability/FETCH_AVAILABILITY_SUCCESS';
const FETCH_AVAILABILITY_ERROR = 'app/listingAvailability/FETCH_AVAILABILITY_ERROR';
const inFlightByStore = new WeakMap();
const AVAILABILITY_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

// ================ Reducer ================ //

const initialState = {
  byId: {},
  revision: 0,
};

const listingAvailabilityReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // Cards are advisory; invalidate them immediately after local mutations.
    // Checkout and transaction calendars continue to query authoritative slots.
    case 'app/CheckoutPage/INITIATE_ORDER_SUCCESS':
    case 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS':
    case 'app/TransactionPage/TRANSITION_SUCCESS':
    case 'app/EditListingPage/UPDATE_LISTING_SUCCESS':
    case 'app/EditListingPage/ADD_AVAILABILITY_EXCEPTION_SUCCESS':
    case 'app/EditListingPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS':
    case 'app/auth/LOGOUT_SUCCESS':
      return { byId: {}, revision: (state.revision || 0) + 1 };
    case FETCH_AVAILABILITY_REQUEST: {
      const { listingId } = payload;
      return {
        ...state,
        byId: {
          ...state.byId,
          [listingId]: { status: 'loading', data: null, error: null },
        },
      };
    }
    case FETCH_AVAILABILITY_SUCCESS: {
      const { listingId, data, revision } = payload;
      if (revision !== (state.revision || 0)) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [listingId]: { status: 'loaded', data, error: null },
        },
      };
    }
    case FETCH_AVAILABILITY_ERROR: {
      const { listingId, error, revision } = payload;
      if (revision !== (state.revision || 0)) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [listingId]: { status: 'error', data: null, error },
        },
      };
    }
    default:
      return state;
  }
};

export default listingAvailabilityReducer;

// ================ Selectors ================ //

export const selectListingAvailability = (state, listingId) =>
  state.listingAvailability?.byId?.[listingId] || null;

// ================ Action creators ================ //

const fetchAvailabilityRequest = listingId => ({
  type: FETCH_AVAILABILITY_REQUEST,
  payload: { listingId },
});

const fetchAvailabilitySuccess = (listingId, data, revision) => ({
  type: FETCH_AVAILABILITY_SUCCESS,
  payload: { listingId, data, revision },
});

const fetchAvailabilityError = (listingId, error, revision) => ({
  type: FETCH_AVAILABILITY_ERROR,
  payload: { listingId, error, revision },
});

const fetchAllTimeSlots = (sdk, params) => {
  const fetchPage = page => sdk.timeslots.query({ ...params, page });

  return fetchPage(1).then(firstResponse => {
    const firstPageSlots = denormalisedResponseEntities(firstResponse);
    const totalPages = firstResponse?.data?.meta?.totalPages || 1;

    if (totalPages <= 1) {
      return firstPageSlots;
    }

    const pagePromises = Array.from({ length: totalPages - 1 }, (_, index) => {
      return fetchPage(index + 2);
    });

    return Promise.all(pagePromises).then(restResponses => {
      const restSlots = restResponses.reduce((picked, response) => {
        return [...picked, ...denormalisedResponseEntities(response)];
      }, []);

      return [...firstPageSlots, ...restSlots];
    });
  });
};

// ================ Thunks ================ //

const isFreshAvailability = availabilityState => {
  if (availabilityState?.status !== 'loaded') {
    return false;
  }

  const lastUpdatedTs = Date.parse(availabilityState?.data?.lastUpdated || '');
  if (Number.isNaN(lastUpdatedTs)) {
    return false;
  }

  return Date.now() - lastUpdatedTs < AVAILABILITY_CACHE_MAX_AGE_MS;
};

export const fetchListingAvailability = listing => (dispatch, getState, sdk) => {
  const listingId = listing?.id?.uuid;
  if (!listingId || !listing?.id) return Promise.resolve(null);

  let pending = inFlightByStore.get(getState);
  if (!pending) {
    pending = new Map();
    inFlightByStore.set(getState, pending);
  }
  const revision = getState().listingAvailability?.revision || 0;
  const key = `${revision}:${listingId}`;
  if (pending.has(key)) return pending.get(key);
  const existing = selectListingAvailability(getState(), listingId);
  if (isFreshAvailability(existing)) {
    return Promise.resolve(existing?.data || null);
  }

  const timeZone = resolveAvailabilityTimeZone({ listing });
  const now = new Date();
  const start = now;
  const end = addTime(start, AVAILABILITY_LOOKAHEAD_DAYS, 'days', timeZone);

  dispatch(fetchAvailabilityRequest(listingId));

  const request = fetchAllTimeSlots(sdk, {
    listingId: listing.id,
    start,
    end,
    perPage: 500,
  })
    .then(response => {
      const timeSlots = response;
      const timeSlotsByDate = timeSlotsPerDate(start, end, timeSlots, timeZone);
      const badgeData = getAvailabilityBadgeData({
        listing,
        availabilityData: {
          availableBookingDate: listing?.attributes?.publicData?.availableBookingDate,
          timeZone,
        },
        timeSlotsByDate,
        timeZone,
        now,
        lookaheadDays: AVAILABILITY_LOOKAHEAD_DAYS,
      });

      const firstBookableDate = badgeData?.firstBookableDateId || null;

      const data = {
        availableTonight: badgeData?.isBookableToday === true,
        firstAvailableDate: firstBookableDate,
        isBookableToday: badgeData?.isBookableToday === true,
        firstBookableDate,
        availableBookingDate: firstBookableDate,
        timeZone,
        lastUpdated: new Date().toISOString(),
      };

      dispatch(fetchAvailabilitySuccess(listingId, data, revision));
      return data;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchAvailabilityError(listingId, error, revision));
      return null;
    })
    .finally(() => {
      if (pending.get(key) === request) pending.delete(key);
    });
  pending.set(key, request);
  return request;
};
